import { Link } from 'react-router-dom';
import { Puzzle } from 'lucide-react';
import { useSpacePlugins } from '../../hooks/usePlugins';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import type { PluginProvenance } from '../../types';

interface PluginManagedBannerProps {
  provenance: PluginProvenance;
}

/**
 * Shown on Skill and Agent detail pages when the entity was installed by a
 * plugin. Tells the user that local edits will be overwritten on the next
 * sync, and links back to the source plugin.
 */
export default function PluginManagedBanner({ provenance }: PluginManagedBannerProps) {
  const spaceId = useSpaceId();
  const { data: pluginsData } = useSpacePlugins();
  const plugin = pluginsData?.items.find(
    (p) => p.pluginId === provenance.pluginId,
  );
  const pluginLabel = plugin?.displayName ?? plugin?.name ?? provenance.pluginId;

  return (
    <div className="flex items-start gap-3 rounded-md border border-border bg-accent/30 px-3 py-2.5">
      <Puzzle className="mt-0.5 h-4 w-4 shrink-0 text-foreground/70" />
      <div className="min-w-0 flex-1 text-sm">
        <p className="text-foreground">
          Managed by the{' '}
          <Link
            to={routes.console.spacePlugin(spaceId, provenance.pluginId)}
            className="font-medium underline hover:no-underline"
          >
            {pluginLabel}
          </Link>{' '}
          plugin.
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Edits made here will be overwritten on the next sync. Update the
          upstream repository to keep your changes.
        </p>
        {provenance.resourceKey && (
          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
            {provenance.resourceKey}
          </p>
        )}
      </div>
    </div>
  );
}
